// Feature Sessions — TypeScript types mirroring backend API schemas

import { getRuntimeConfig } from "../config/runtime-config";

/**
 * Default GitHub repository (owner/repo) from the runtime config; empty string
 * in mock mode. A function, not a constant: runtime config resolves during app
 * bootstrap, after this module is imported.
 */
export function getDefaultRepository(): string {
  return getRuntimeConfig().githubRepository;
}

/** Interaction tier of a session. "chat" = in-app relay; "remote_control" =
 * a Claude Code Remote Control container the owner drives from claude.ai/code. */
export type SessionKind = "chat" | "remote_control";

export interface FeatureBranch {
  branch_name: string;
  repository: string;
  owner_user_id: string;
  visibility_scope: string;
  session_kind: SessionKind;
  feedback_ids: string[];
  last_known_pr_url: string | null;
  active_environment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateFeatureBranchRequest {
  branch_name: string;
  repository: string;
  base_branch?: string;
  feedback_ids?: string[];
  visibility_scope?: string;
  session_kind?: SessionKind;
}

/** Remote Control: whether the container is active and signed in. */
export interface RemoteControlStatus {
  active: boolean;
  logged_in: boolean;
}

/** Remote Control: auth URL + login id returned to start the brokered login. */
export interface LoginPrompt {
  login_id: string;
  auth_url: string;
}

export interface SessionResponse {
  environment_id: string;
  branch_name: string;
  commit_sha: string | null;
  preview_url: string | null;
  started_at: string;
}

export interface SaveResponse {
  commit_sha: string;
  branch_name: string;
  committed_at: string;
}

export interface SessionIndicators {
  unsaved_changes_present: boolean;
  unpushed_commits_present: boolean;
  loaded_commit_sha: string | null;
}

export interface PreviewStatus {
  preview_url: string;
  healthy: boolean;
}

export interface ProvisioningStatus {
  /** The current provisioning stage, or null when not provisioning. */
  stage: string | null;
  /** Human-readable label for the current stage, or null when not provisioning. */
  label: string | null;
}

export interface RestorePointItem {
  sha: string;
  subject: string;
  committed_at: string;
  turn_idx: number;
}

export interface RestorePointsListResponse {
  items: RestorePointItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface RewindRequest {
  target_sha: string;
}

export interface RewindResponse {
  from_sha: string;
  to_sha: string;
  committed_at: string;
}

export interface OpenPullRequestRequest {
  title: string;
  body: string;
  base_branch?: string;
}

export interface PullRequestResponse {
  pr_number: number;
  url: string;
  title: string;
  state: string;
  branch_name: string;
  created_at: string;
}

export interface SharePreviewRequest {
  visibility_scope?: string;
  expires_at?: string | null;
}

export interface SharedPreviewResponse {
  branch_name: string;
  preview_url: string | null;
  preview_healthy: boolean;
  share_token: string;
  visibility_scope: string;
  expires_at: string | null;
}

export interface StreamChunk {
  chunk_type: "text" | "status" | "error" | "result";
  content: string;
  session_id?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  isStreaming?: boolean;
}
